/**
 * MiterMappingPanel
 *
 * Settings → Miter tab. Maps our internal technicians to Miter
 * team-member IDs (the `tm_xxx` identifiers Miter expects in the
 * timesheet POST body). Lives behind a Settings tab so admins can
 * verify mappings BEFORE the weekly batch push goes live.
 *
 * What this panel surfaces:
 *   • Top — Miter config banner (mode: dry_run / sandbox / live)
 *   • Middle — Remote Team Members pull from Miter's
 *     GET /api/v2/team_members. When the API key lacks the
 *     `team_members:read:batch` scope this gracefully degrades to a
 *     "scope required" notice + a manual-paste fallback.
 *   • Bottom — Per-tech mapping rows: tech name + email on the left,
 *     Miter team-member dropdown (or manual ID input) on the right.
 *     Saves immediately on change via PUT /api/miter/map-technician.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Link2, Cloud, ExternalLink, Check, ChevronsUpDown, Briefcase, Users } from "lucide-react";
import { SettingsPanel, PanelHeader, SubTabs, Callout, CollapsibleSection } from "@/components/settings/SettingsUI";
import {
  getMiterConfigStatus, getMiterMappingStatus, getMiterRemoteTeamMembers, mapMiterTechnician,
  resyncMiterTechnician,
  getMiterJobs, syncMiterJobs, upsertMiterJobMapping, getMiterJobMappings,
  getWorkOrdersGlobal,
} from "@/lib/api";

const ModeBadge = ({ mode }) => {
  const map = {
    dry_run:  { label: "DRY-RUN",  color: "bg-amber-100 text-amber-800 border-amber-200" },
    sandbox:  { label: "SANDBOX",  color: "bg-blue-100  text-blue-800  border-blue-200"  },
    live:     { label: "LIVE",     color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  };
  const m = map[mode] || map.dry_run;
  return (
    <Badge variant="outline" className={`${m.color} font-mono text-[10px] tracking-wider px-2 py-0.5`} data-testid={`miter-mode-${mode}`}>
      {m.label}
    </Badge>
  );
};

const MITER_TABS = [
  { id: "team", label: "Team Members", icon: Users },
  { id: "jobs", label: "Jobs", icon: Briefcase },
];

export default function MiterMappingPanel() {
  const [tab, setTab] = useState("team");
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [remoteTMs, setRemoteTMs] = useState({ success: false, team_members: [], error: "" });
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [resyncingId, setResyncingId] = useState(null);

  const loadAll = useCallback(async () => {
    const [cfg, st] = await Promise.all([
      getMiterConfigStatus().catch(() => ({ data: null })),
      getMiterMappingStatus().catch(() => ({ data: null })),
    ]);
    setConfig(cfg.data);
    setStatus(st.data);
  }, []);

  const fetchRemote = useCallback(async () => {
    setLoadingRemote(true);
    try {
      const r = await getMiterRemoteTeamMembers();
      setRemoteTMs(r.data || { success: false, team_members: [] });
    } catch (e) {
      setRemoteTMs({ success: false, team_members: [], error: e?.message || "Failed" });
    } finally {
      setLoadingRemote(false);
    }
  }, []);

  useEffect(() => { loadAll(); fetchRemote(); }, [loadAll, fetchRemote]);

  // Sort team-members alphabetically once — the Combobox uses this list.
  const sortedTMs = useMemo(() => {
    const list = [...(remoteTMs.team_members || [])];
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return list;
  }, [remoteTMs.team_members]);

  // Auto-suggest mappings: match our tech's email to a Miter team member's email
  const suggestionsByTechId = useMemo(() => {
    const out = {};
    if (!remoteTMs.success || !status?.technicians) return out;
    const byEmail = new Map(sortedTMs.filter(tm => tm.email).map(tm => [tm.email.toLowerCase(), tm]));
    status.technicians.forEach(t => {
      const e = (t.email || "").toLowerCase();
      if (e && byEmail.has(e) && !t.miter_employee_id) out[t.id] = byEmail.get(e);
    });
    return out;
  }, [remoteTMs.success, sortedTMs, status]);

  const saveMapping = async (techId, fields) => {
    setSavingId(techId);
    try {
      await mapMiterTechnician({ tech_id: techId, ...fields });
      toast.success("Mapping saved");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  // iter194 — One-click "Re-sync from Miter". Hits the live Miter
  // team_members endpoint, matches by email, and stamps the canonical
  // miter_employee_id. Toast surfaces what changed (or "no changes" /
  // "orphan flagged") so the operator gets immediate feedback.
  const resyncTech = async (techId, techName) => {
    setResyncingId(techId);
    try {
      const r = await resyncMiterTechnician(techId);
      const d = r.data || {};
      if (d.matched && Object.keys(d.changes || {}).length > 0) {
        toast.success(
          `${techName}: ${d.message}` +
          (d.miter_name && d.miter_name !== techName ? ` (Miter has them as "${d.miter_name}")` : ""),
        );
      } else if (d.matched) {
        toast.success(`${techName}: already up-to-date`);
      } else {
        toast.warning(`${techName}: ${d.message}`, { duration: 8000 });
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
    } finally {
      setResyncingId(null);
    }
  };

  if (!config) {
    return <div className="text-sm text-slate-500 p-4" data-testid="miter-mapping-loading">Loading Miter config…</div>;
  }

  const mode = config.mode || "dry_run";
  const needsScope = remoteTMs.error?.includes("scope") || remoteTMs.status_code === 403;
  const techs = status?.technicians || [];

  return (
    // iter298 — dressed to match the Foundation group: shared SubTabs
    // strip above the card, PanelHeader inside it, and collapsible
    // sections in the body. Layout only — every handler is untouched.
    <div className="space-y-3" data-testid="miter-mapping-panel">
      <SubTabs
        items={MITER_TABS}
        value={tab}
        onChange={setTab}
        testId="miter-subtabs"
        testIdPrefix="miter-subtab"
      />

      <SettingsPanel>
        <PanelHeader
          icon={Cloud}
          title="Miter"
          hint="Timekeeping — team member and job mapping"
          actions={
            <>
              <ModeBadge mode={mode} />
              <span className="text-[11px] text-slate-500 font-mono hidden md:inline">{config.base_url_host}</span>
              <span className="text-[11px] flex items-center gap-1.5">
                <span className={config.has_api_key ? "text-emerald-700" : "text-red-600"}>
                  {config.has_api_key ? "✓" : "✗"} API key
                </span>
                <span className="text-slate-300">·</span>
                <span className={config.has_base_url ? "text-emerald-700" : "text-red-600"}>
                  {config.has_base_url ? "✓" : "✗"} Base URL
                </span>
                <span className="text-slate-300">·</span>
                <span className={config.has_company_id ? "text-emerald-700" : "text-slate-500"}>
                  {config.has_company_id ? "✓" : "○"} Company ID
                </span>
              </span>
            </>
          }
        />

        <div className="p-4 space-y-3">
          {mode === "dry_run" && (
            <Callout tone="warn" testId="miter-dry-run-callout">
              <strong>Dry-run mode</strong> — no real payroll calls will be made. Set{" "}
              <code className="font-mono bg-amber-100 px-1 rounded">MITER_DRY_RUN=false</code> in the
              backend env to go live.
            </Callout>
          )}

          {tab === "team" && (
            <>
      {/* Remote team-members fetch state */}
      <CollapsibleSection
        id="miter-remote-tms" testId="miter-remote-tms-section"
        icon={Link2} title="Miter Team Members"
        extra={remoteTMs.success && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="miter-remote-count">
                {remoteTMs.count} found
              </Badge>
            )}
        actions={
          <Button variant="outline" size="sm" onClick={fetchRemote} disabled={loadingRemote} data-testid="btn-refresh-remote-tms">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingRemote ? "animate-spin" : ""}`} />
            Refresh from Miter
          </Button>
        }
      >
        {needsScope ? (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 space-y-2" data-testid="miter-scope-warning">
            <div className="font-semibold flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Scope required: <code className="font-mono">team_members:read:batch</code>
            </div>
            <p>
              Your Miter API token authenticates but doesn't have permission to list team members.
              Open the Miter admin console → API tokens, edit the token used here, and add these scopes:
            </p>
            <ul className="list-disc ml-5 space-y-0.5 font-mono text-[11px]">
              <li>team_members:read:batch</li>
              <li>jobs:read:batch</li>
              <li>timesheets:create:single</li>
              <li>timesheets:read:batch</li>
            </ul>
            <p>Once added, click Refresh from Miter. Meanwhile, you can paste team-member IDs manually below.</p>
          </div>
        ) : !remoteTMs.success ? (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="miter-remote-error">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
            {remoteTMs.error || "Failed to fetch team members from Miter"}
          </div>
        ) : (
          <div className="text-xs text-slate-500">
            Pulled {remoteTMs.count} active team members from Miter. Use the dropdowns below to map each of our techs to a Miter team member.
          </div>
        )}
      </CollapsibleSection>

      {/* Mapping table */}
      <CollapsibleSection
        id="miter-tech-mapping" testId="miter-tech-mapping-section"
        icon={Users} title="Technician Mapping"
        extra={
            <Badge variant="outline" className="text-[10px]" data-testid="miter-mapping-counts">
              {status?.mapped || 0}/{status?.total_active || 0} mapped
            </Badge>
          }
      >
        {techs.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center" data-testid="miter-no-techs">
            No active technicians found. Add technicians under the General tab first.
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {techs.map(t => {
              const suggestion = suggestionsByTechId[t.id];
              const isMapped = !!(t.miter_employee_id || "").trim();
              return (
                <div key={t.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`miter-tech-row-${t.id}`}>
                  <div className="col-span-4 min-w-0">
                    <div className="font-medium text-sm text-slate-800 flex items-center gap-1.5">
                      {isMapped && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                      <span className="truncate">{t.name}</span>
                    </div>
                    {t.email && <div className="text-xs text-slate-500 truncate">{t.email}</div>}
                  </div>
                  <div className="col-span-5">
                    {remoteTMs.success && sortedTMs.length > 0 ? (
                      <MiterTMCombobox
                        techId={t.id}
                        value={t.miter_employee_id || ""}
                        teamMembers={sortedTMs}
                        onSelect={(id) => saveMapping(t.id, { miter_employee_id: id })}
                      />
                    ) : (
                      <Input
                        defaultValue={t.miter_employee_id || ""}
                        placeholder="tm_xxx (paste Miter team-member ID)"
                        className="h-8 text-xs font-mono"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (t.miter_employee_id || "")) saveMapping(t.id, { miter_employee_id: v });
                        }}
                        data-testid={`input-miter-tm-${t.id}`}
                      />
                    )}
                  </div>
                  <div className="col-span-3 text-right flex items-center justify-end gap-2">
                    {suggestion && !isMapped && (
                      <Button
                        size="sm" variant="outline"
                        className="h-7 text-[11px] text-blue-700 border-blue-200 hover:bg-blue-50"
                        onClick={() => saveMapping(t.id, { miter_employee_id: suggestion.id })}
                        disabled={savingId === t.id}
                        data-testid={`btn-accept-suggestion-${t.id}`}
                      >
                        Suggest: {suggestion.name}
                      </Button>
                    )}
                    {isMapped && (
                      <span className="text-[10px] font-mono text-slate-400 truncate" title={t.miter_employee_id}>
                        {t.miter_employee_id.length > 10 ? `${t.miter_employee_id.slice(0, 8)}…` : t.miter_employee_id}
                      </span>
                    )}
                    {/* iter194 — Per-row Re-sync. Re-fetches the Miter
                        team_member by email and stamps the canonical
                        24-char hex id. Disabled when the tech has no
                        email (can't match) or another row is mid-flight. */}
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-slate-800"
                      onClick={() => resyncTech(t.id, t.name)}
                      disabled={resyncingId === t.id || !(t.email || "").trim()}
                      title={
                        !(t.email || "").trim()
                          ? "Add an email to this tech first — re-sync matches by email."
                          : "Re-sync this technician from Miter (match by email)"
                      }
                      data-testid={`btn-resync-miter-${t.id}`}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${resyncingId === t.id ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <ExternalLink className="w-3 h-3" />
        Each tech also has Department / Timezone / Default Job fields available on their full record under Settings → General → Technicians.
      </div>
            </>
          )}

          {tab === "jobs" && <MiterJobsTab />}
        </div>
      </SettingsPanel>
    </div>
  );
}

/**
 * Searchable single-select combobox for Miter team members. Trigger
 * shows the currently-selected team-member or "— Not mapped —"; opening
 * the popover reveals an alphabetized list with a fuzzy search input.
 * `cmdk` (the lib backing shadcn Command) does the filtering against
 * each CommandItem's `value` prop, so we cram name + employee number
 * + email + title into `value` for broad matching.
 */
function MiterTMCombobox({ techId, value, teamMembers, onSelect }) {
  const [open, setOpen] = useState(false);
  const selected = teamMembers.find(tm => tm.id === value);
  const triggerLabel = selected
    ? `${selected.name}${selected.employee_number ? ` · #${selected.employee_number}` : ""}`
    : "— Not mapped —";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 justify-between text-xs font-normal"
          data-testid={`combobox-miter-tm-${techId}`}
        >
          <span className={`truncate ${!selected ? "text-slate-400" : ""}`}>{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search name, employee #, email..." className="h-9 text-xs" data-testid={`combobox-input-${techId}`} />
          <CommandList>
            <CommandEmpty>No team member found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__unset__"
                onSelect={() => { onSelect(""); setOpen(false); }}
                className="text-xs text-slate-500 italic"
                data-testid={`combobox-item-unset-${techId}`}
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${!value ? "opacity-100" : "opacity-0"}`} />
                — Not mapped —
              </CommandItem>
              {teamMembers.map(tm => {
                // Cram all searchable fields into `value` so cmdk's fuzzy
                // matcher finds the row whether the user types name,
                // employee number, email, or job title.
                const searchValue = [tm.name, tm.employee_number, tm.email, tm.title].filter(Boolean).join(" ");
                return (
                  <CommandItem
                    key={tm.id}
                    value={searchValue}
                    onSelect={() => { onSelect(tm.id); setOpen(false); }}
                    className="text-xs"
                    data-testid={`combobox-item-${techId}-${tm.id}`}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${value === tm.id ? "opacity-100" : "opacity-0"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">
                        <span className="font-medium">{tm.name}</span>
                        {tm.employee_number && <span className="text-slate-400 ml-1.5">#{tm.employee_number}</span>}
                      </div>
                      {(tm.email || tm.title) && (
                        <div className="text-[10px] text-slate-500 truncate">
                          {tm.title}{tm.title && tm.email ? " · " : ""}{tm.email}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}



/**
 * #51 — Miter Jobs tab.
 *
 * Shows the cached Miter `/api/v2/jobs` list and lets admins override
 * the default job-per-WO mapping. The cache lives in Mongo
 * (`miter_jobs`) so the table renders instantly; "Refresh from Miter"
 * hits POST /miter/jobs/sync to pull the latest. Overrides land in
 * `miter_job_mappings` keyed by local WO number.
 */
function MiterJobsTab() {
  const [jobs, setJobs] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [mappings, setMappings] = useState({});
  const [wos, setWOs] = useState([]);
  const [loadingSync, setLoadingSync] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    const [jr, mr, wr] = await Promise.all([
      getMiterJobs().catch(() => ({ data: { jobs: [], last_synced_at: null } })),
      getMiterJobMappings().catch(() => ({ data: { mappings: [] } })),
      getWorkOrdersGlobal().catch(() => ({ data: [] })),
    ]);
    setJobs(jr.data?.jobs || []);
    setLastSyncedAt(jr.data?.last_synced_at);
    setLastSyncStatus(jr.data?.last_sync_status);
    const m = {};
    (mr.data?.mappings || []).forEach(row => { m[row.local_key] = row.miter_job_id; });
    setMappings(m);
    // Filter out completed/cancelled — Miter mappings only matter for
    // active work the team is currently logging time against.
    setWOs((wr.data || []).filter(w => !["completed", "cancelled"].includes(w.status)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sortedJobs = useMemo(() => {
    const list = [...jobs];
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return list;
  }, [jobs]);

  const filteredWOs = useMemo(() => {
    if (!filter.trim()) return wos;
    const q = filter.toLowerCase();
    return wos.filter(w =>
      (w.wo_number || "").toLowerCase().includes(q) ||
      (w.title || "").toLowerCase().includes(q) ||
      (w.client_name || "").toLowerCase().includes(q)
    );
  }, [wos, filter]);

  const runSync = async () => {
    setLoadingSync(true);
    try {
      const r = await syncMiterJobs();
      if (r.data?.success) {
        toast.success(`Pulled ${r.data.count} jobs from Miter`);
      } else {
        toast.error(`Sync failed: ${r.data?.error || "unknown error"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setLoadingSync(false);
    }
  };

  const saveMapping = async (localKey, miterJobId) => {
    setSavingKey(localKey);
    try {
      await upsertMiterJobMapping({ local_key: localKey, miter_job_id: miterJobId || "" });
      setMappings(prev => {
        const next = { ...prev };
        if (miterJobId) next[localKey] = miterJobId;
        else delete next[localKey];
        return next;
      });
      toast.success(miterJobId ? "Mapping saved" : "Mapping cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="miter-jobs-tab">
      <CollapsibleSection
        id="miter-jobs" testId="miter-jobs-section"
        icon={Briefcase} title="Miter Jobs"
        extra={<>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="miter-jobs-count">
              {jobs.length} cached
            </Badge>
            {lastSyncedAt && (
              <span className="text-[10px] text-slate-400 font-normal" data-testid="miter-jobs-last-synced">
                Last synced: {new Date(lastSyncedAt).toLocaleString()}
              </span>
            )}
            {lastSyncStatus && lastSyncStatus !== "ok" && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                {lastSyncStatus}
              </Badge>
            )}
          </>}
        actions={
          <Button variant="outline" size="sm" onClick={runSync} disabled={loadingSync} data-testid="btn-sync-miter-jobs">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingSync ? "animate-spin" : ""}`} />
            Refresh from Miter
          </Button>
        }
      >

        <div className="text-xs text-slate-600 mb-3">
          Map specific work orders to a Miter job ID to override the default time-allocation. Leave blank to use the tech's default job.
        </div>

        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by WO #, title, or customer..."
          className="h-8 text-xs mb-3 max-w-[420px]"
          data-testid="input-jobs-filter"
        />

        {filteredWOs.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center" data-testid="miter-no-wos">
            No active work orders match the filter.
          </div>
        ) : (
          <Table data-testid="miter-jobs-mapping-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs w-[120px]">WO #</TableHead>
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Customer</TableHead>
                <TableHead className="text-xs w-[300px]">Miter Job</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWOs.slice(0, 200).map(wo => {
                const key = wo.wo_number || wo.id;
                const mapped = mappings[key] || "";
                return (
                  <TableRow key={wo.id} data-testid={`miter-job-row-${wo.id}`}>
                    <TableCell className="font-mono text-xs font-bold text-blue-700">{wo.wo_number}</TableCell>
                    <TableCell className="text-xs">{wo.title || "Untitled"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{wo.client_name}</TableCell>
                    <TableCell>
                      <Select
                        value={mapped || "__none__"}
                        onValueChange={(v) => saveMapping(key, v === "__none__" ? "" : v)}
                        disabled={savingKey === key || sortedJobs.length === 0}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`select-miter-job-${wo.id}`}>
                          <SelectValue placeholder={sortedJobs.length === 0 ? "Sync Miter jobs first" : "— Default —"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__" className="text-xs italic text-slate-500">— Default —</SelectItem>
                          {sortedJobs.filter(j => (j.name || j.id || "").trim()).map(j => (
                            <SelectItem key={j.id} value={j.id} className="text-xs">
                              <span className="font-medium">{j.name}</span>
                              {j.code && <span className="text-slate-400 ml-1.5">#{j.code}</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {filteredWOs.length > 200 && (
          <div className="text-[10px] text-slate-400 mt-2">
            Showing first 200 of {filteredWOs.length} — refine the filter to narrow down.
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
}
